import React from "react";
import { Icon } from "@/components";
import { IconButton, Switch, TimePicker } from "@/ui";
import { TimeSlotListProps, ValidationError } from "context/schedule/types";
import styles from "./WorkingHour.module.scss";

const PlusIcon = () => <Icon icon="plus" />;
const DeleteIcon = () => <Icon icon="delete" />;

const TimeSlotList: React.FC<TimeSlotListProps> = ({
    timeSlots = [],
    errors = {},
    active,
    onChangeSlot,
    onRemoveSlot,
    onAddSlot,
}) => {
    const handleSlotChange = (index: number, field: string, value: any) => {
        onChangeSlot(index, field as any, value);
    };

    const getSlotError = (index: number, field: string): string | undefined => {
        const slotErrors = errors[index] as ValidationError | undefined;
        return slotErrors?.[field];
    };

    const gridClass = active
        ? `${styles.slotsGrid} ${styles.slotsGridExpress}`
        : styles.slotsGrid;

    return (
        <div className={styles.slots}>
            <div
                className={styles.slotsScroll}
                data-express={active ? "true" : undefined}
            >
                <div className={styles.slotsScrollInner}>
                    <div className={gridClass}>
                        <span className={styles.slotsHead}>Disable</span>
                        {active ? (
                            <span className={styles.slotsHead}>Express</span>
                        ) : null}
                        <span className={styles.slotsHead}>Starts At</span>
                        <span className={styles.slotsHead}>Finish At</span>
                        <span className={styles.slotsHead}>Cut Off Time</span>
                        <span className={styles.slotsHeadAction}>
                            <IconButton
                                aria-label="Add time slot"
                                onClick={onAddSlot}
                                size="sm"
                                type="button"
                                variant="tertiary"
                            >
                                <PlusIcon />
                            </IconButton>
                        </span>
                    </div>

                    {timeSlots.map((slot, index) => {
                        const key = `${slot.day_of_the_week}-${index}`;
                        return (
                            <div className={gridClass} key={key}>
                                <div className={styles.slotSwitch}>
                                    <Switch
                                        checked={!!slot.disable}
                                        label="Disable"
                                        name={`${key}-disable`}
                                        onCheckedChange={checked =>
                                            handleSlotChange(
                                                index,
                                                "disable",
                                                checked,
                                            )
                                        }
                                        showLabel={false}
                                        variant="danger"
                                    />
                                </div>
                                {active ? (
                                    <div className={styles.slotSwitch}>
                                        <Switch
                                            checked={!!slot.is_express}
                                            label="Express"
                                            name={`${key}-express`}
                                            onCheckedChange={checked =>
                                                handleSlotChange(
                                                    index,
                                                    "is_express",
                                                    checked,
                                                )
                                            }
                                            showLabel={false}
                                        />
                                    </div>
                                ) : null}
                                <TimePicker
                                    className={styles.slotInput}
                                    error={getSlotError(index, "start")}
                                    fullWidth
                                    label="Starts At"
                                    name={`${key}-start`}
                                    onChange={value =>
                                        handleSlotChange(index, "start", value)
                                    }
                                    value={slot.start || ""}
                                />
                                <TimePicker
                                    className={styles.slotInput}
                                    error={getSlotError(index, "end")}
                                    fullWidth
                                    label="Finish At"
                                    name={`${key}-end`}
                                    onChange={value =>
                                        handleSlotChange(index, "end", value)
                                    }
                                    value={slot.end || ""}
                                />
                                <TimePicker
                                    className={styles.slotInput}
                                    error={getSlotError(index, "cutoff_time")}
                                    fullWidth
                                    label="Cut Off Time"
                                    name={`${key}-cutoff_time`}
                                    onChange={value =>
                                        handleSlotChange(
                                            index,
                                            "cutoff_time",
                                            value,
                                        )
                                    }
                                    value={slot.cutoff_time || ""}
                                />
                                <div className={styles.slotAction}>
                                    <IconButton
                                        aria-label={`Remove time slot ${
                                            index + 1
                                        }`}
                                        onClick={() => onRemoveSlot(index)}
                                        size="sm"
                                        type="button"
                                        variant="tertiary"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        );
                    })}

                    {timeSlots.length === 0 ? (
                        <div className={styles.slotsEmpty}>
                            <p>No time slots are scheduled for this day</p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default TimeSlotList;
